// What provisioning step 3 DOES to a customer's access — ADR-0014 §7.1 step 3, since the step turned from a
// record into a gate (`ApplicationEntitlementGate`, task b260fddb).
//
// WHY THIS FILE EXISTS AT ALL. Step 3 is a REPLACE: `PUT …/provisioning/entitlements` stores exactly the list
// it is given. While entitlements were bookkeeping, a save that sent an empty list was a bookkeeping change.
// It is not any more — a customer tenant without the `clms` code is served NOTHING on `/api` or `/scorm`, its
// pääkäyttäjä exactly as its learners, because the gate is not an authorization decision about a person
// (`ApplicationEntitlementGate.Admits`). So one checkbox on this console now shuts a customer, and the two
// questions that follow — may the operator do it unasked, and can anyone SEE that it is done — are decided
// here rather than inside a component, so that they are decided once and can be tested without a DOM.
//
// THE DECISION THIS FILE ENCODES (task aa0c3eb7):
//
//   1. REMOVING THE ENTITLEMENT IS ALLOWED AND IS CONFIRMED, not blocked in favour of the freeze. A contract
//      that has ended is a real commercial fact and step 3 is the only place that records it; a console that
//      refused it would leave Verso with no way to say "this customer no longer buys CLMS" except by freezing
//      a tenant whose contract is not suspended but over. But the CONSEQUENCE is the freeze's consequence, so
//      the guard is the freeze's guard — see `TenantFreezeSection`, which asks for the slug typed out. If the
//      cheaper-looking path had the weaker guard, the checkbox grid would simply become the way to shut a
//      customer without the freeze's ceremony, which is the defect rather than a fix for it.
//
//      The one asymmetry with the freeze is the REASON, and it is the API's: `SetTenantEntitlementsRequest`
//      has no reason field and the audit row records who, when and the before/after set. The console does not
//      invent a field the API would drop — it names the freeze, which does carry one, as the path when the
//      point is to shut a customer temporarily and say why.
//
//      AND THE CONFIRMATION IS NOT THE GUARANTEE, which is the correction this task's second round made.
//      Everything below decides over a set the console READ, and a page left open while another operator
//      answered step 3 holds the wrong one — so the dangerous save is precisely the one that does not look
//      dangerous here: a page showing a closed set over a tenant somebody has since opened, where the
//      selection equals what this page believes is stored and no confirmation is therefore due. The console
//      narrows that window (the close path re-reads before it asks) and the SERVER closes it: step 3 takes
//      the set the decision was made over and refuses a replace whose expectation has moved
//      (`entitlements_changed_concurrently`), from the replacing statement's own `WHERE` — so two saves
//      that overlap, both decided over the same set, cannot both land. A guard that only one end holds is
//      a guard the other end can be out of date about.
//
//   2. THE STATE IS SAID WHERE IT IS TRUE, and it is said in TWO forms, because a shut tenant is two
//      different facts about Verso. The set alone cannot tell them apart — an empty one is what provisioning
//      step 1 leaves behind AND what removing the last application leaves behind — so the backend records
//      WHEN step 3 last answered (`tenants.entitlements_recorded_at`, backfilled from the audit chain) and
//      {@link accessState} reads the two together:
//
//        * NEVER OPENED — no answer is on record. The onboarding has not reached step 3; nobody decided
//          this. It is an unfinished provisioning and the console says so, which is the sentence a customer
//          waiting to be let in needs somebody to see.
//        * CLOSED — an answer IS on record and it does not include the application. Somebody decided it:
//          the applications were taken away, or only `hrm` was ever bought. The console dates it.
//
//      What the console still does not say is WHY — no reason is recorded anywhere, because this request has
//      no field for one — and it does not guess at a contract ending. "Recorded, and it does not include
//      this application" is the whole of what is known.
//
//   3. THE DECISION IS READ HERE AND NOT ASKED OF THE BACKEND, and that is deliberate on the backend's own
//      terms: `ApplicationEntitlementGateGuardTests` rules 3 and 4 keep the admission question inside
//      `ApplicationEntitlementGate` and its one asking site, so no DTO may carry a "closed" flag. What the
//      platform API reports is step 3's answer; the rule for reading it is one line, it lives here, and
//      `tenant-access.test.mjs` pins it against the gate's own C# source.
//
//   4. THERE ARE TWO DOORS NOW, AND THE CONSOLE STILL REPORTS ONE STATE — ADR-0028 §4 added
//      `/api/integrations/v1` → `ApplicationEntitlements.Board` to the gate's map, so a tenant's recorded set
//      opens and shuts two surfaces, not one (task d87f7559). What follows is a product decision and not a
//      mechanical one, so it is written down here:
//
//        * THE STATE STAYS THE PRODUCT'S. `accessState` reads `clms` and nothing else, and the badge on the
//          directory row, the page header and the nav is that one state. A second badge per gated code would
//          put a pill on the directory for a surface that has no users to be shut out of it — Verso Board is
//          one machine reading over a token, and an integration that is refused reports its own 403 to the
//          operator who runs it. The badge is a POINTER ("this customer is not being served"); the claim is
//          the step's notice, and that is where the second door is named.
//        * WHAT CHANGED IS THE CLAIM, NOT THE STATE. The sentences used to say "every request is refused",
//          which was true while one code gated everything and is not true of a tenant holding `board`. They
//          now say which door is shut, and {@link otherDoorsOpen} names the ones that are not — in the step-3
//          notice and in the close confirmation's effects, which is exactly where an operator decides.
//        * AND THE LIST OF DOORS IS PINNED TO THE GATE, not to this reading of it. {@link GATED_ENTITLEMENTS}
//          is `ApplicationFor`'s non-null range, read out of the C# source by `tenant-access.test.mjs`. A
//          fourth branch in that map fails this console's tests until somebody decides what it says about it,
//          which is the property the old single-door guard had and the reason it was written as a guard.
//
// Deliberately free of runtime imports, so `tenant-access.test.mjs` can drive it under Node's own test
// runner with --experimental-strip-types (this app has no jsdom/RTL setup — see package.json `test`).

/**
 * The entitlement code that decides access TO THIS PRODUCT — the door every sentence this console says about
 * access is about.
 *
 * Mirrors the backend's gate: `ApplicationEntitlementGate.ApplicationFor` maps the tenant-scoped surface onto
 * `ApplicationEntitlements.Clms` by DEFAULT — every `/api` and `/scorm` path that is not one of the two named
 * exceptions — and `Allows` then asks whether the tenant's recorded set contains it. That default is why this
 * one code is the console's access state: it is the door the customer's own people come through, and the only
 * one whose absence locks a pääkäyttäjä and a learner out of everything they use.
 *
 * `hrm` gates nothing and that is not a special case — there is no HRM surface in this backend to map — so
 * unticking it changes a commercial record and nothing else.
 *
 * `tenant-access.test.mjs` reads the C# gate and pins this against it: a console whose notice named a code
 * the door does not ask for would promise an access state the product does not have.
 */
export const ACCESS_ENTITLEMENT = "clms";

/**
 * Every code the backend's gate can ASK for — the whole non-null range of
 * `ApplicationEntitlementGate.ApplicationFor`, in the vocabulary's own order
 * (`ApplicationEntitlements.All`).
 *
 * THERE IS MORE THAN ONE DOOR SINCE ADR-0028 §4, and this list is how the console knows it. `ApplicationFor`
 * now has a named exception below the platform core's: `/api/integrations/v1` maps onto
 * `ApplicationEntitlements.Board`, so a tenant's recorded set opens or shuts TWO surfaces independently —
 * the product's, which {@link ACCESS_ENTITLEMENT} is about, and Verso Board's read surface.
 *
 * What it is NOT is a second access state. The console reports one, and it is the product's
 * ({@link accessState}) — see this file's decision 4. This list exists so that the sentences which used to
 * claim "every request is refused" can name the doors they do not speak for, and so that the day a FOURTH
 * branch appears in that map, `tenant-access.test.mjs` fails until this console has accounted for it rather
 * than quietly keeping a claim that has stopped being true.
 */
export const GATED_ENTITLEMENTS = ["clms", "board"] as const;

/**
 * The gated surfaces OTHER than this product's that a tenant with these recorded entitlements still reaches —
 * the doors a closed access notice, and a save that closes it, may not claim to have shut.
 *
 * Exact per code, like the gate's `StringComparison.Ordinal` and for the same reason {@link accessClosed} is:
 * a stored `"Board"` is a set the door refuses, and claiming the Board surface is open off it would be the
 * one reading that lies in the reassuring direction.
 *
 * Empty for the ordinary customer, which is what keeps the extra sentence off the ordinary screen: `{clms}`
 * and `{clms, hrm}` hold no other door, so nothing is added for them.
 */
export function otherDoorsOpen(
  entitlements: readonly string[] | null | undefined,
): readonly string[] {
  const held = entitlements ?? [];
  return GATED_ENTITLEMENTS.filter(
    (code) => code !== ACCESS_ENTITLEMENT && held.some((recorded) => recorded === code),
  );
}

/**
 * Is a tenant with these recorded entitlements shut out of the product?
 *
 * The membership test is EXACT, like the gate's (`StringComparison.Ordinal`). Folding case here would be the
 * one reading that lies in the dangerous direction: a stored `"CLMS"` is a set the door refuses, and a console
 * that normalised it would report the customer as served while every request of theirs is being rejected.
 *
 * An empty set is the commonest closed state and not an edge case: it is what provisioning step 1 leaves
 * behind until step 3 says what the customer bought.
 */
export function accessClosed(entitlements: readonly string[] | null | undefined): boolean {
  return !(entitlements ?? []).some((code) => code === ACCESS_ENTITLEMENT);
}

/**
 * Would saving `next` over `current` take a served customer's access away? The question the confirmation is
 * asked on, and the ONLY save that gets one.
 *
 * Three saves deliberately do not: adding an entitlement, changing `hrm` alone, and re-sending a set that is
 * already closed — the last because there is nothing left to close, and a confirmation on a tenant whose step
 * 3 has never been done would ask an operator to approve a state their save does not create. What this leaves
 * confirmed is exactly the transition from served to shut.
 */
export function closesAccess(
  current: readonly string[] | null | undefined,
  next: readonly string[] | null | undefined,
): boolean {
  return !accessClosed(current) && accessClosed(next);
}

/**
 * The other direction: this save gives a shut customer its access back. Not a confirmation — opening doors
 * needs no ceremony, for the reason thawing does not (`TenantFreezeSection`) — but the save's own success note
 * says so, because "the entitlements were saved" is the sentence that hid the closing in the first place and
 * would hide the reopening just as well.
 */
export function opensAccess(
  current: readonly string[] | null | undefined,
  next: readonly string[] | null | undefined,
): boolean {
  return accessClosed(current) && !accessClosed(next);
}

/**
 * Are these two recorded answers the SAME answer? Order-insensitive and exact per code, like the set the
 * server stores (it normalises and de-duplicates before writing).
 *
 * It is what tells "the step-3 answer moved under this page" from "it did not" — the question the console
 * asks before it puts a confirmation in front of an operator, so that the thing being confirmed is a state
 * the server still has. The server asks it too, on the write, and that one is the guarantee: this one only
 * decides whether to ask the question.
 */
export function sameEntitlements(
  a: readonly string[] | null | undefined,
  b: readonly string[] | null | undefined,
): boolean {
  const left = [...new Set(a ?? [])].sort();
  const right = [...new Set(b ?? [])].sort();
  return left.length === right.length && left.every((code, i) => code === right[i]);
}

/**
 * What a tenant's access state IS, in the three forms the console has to say differently.
 *
 * `served` — the customer is being let in. `closed` — it is not, and step 3 has an answer on record, so
 * somebody decided this. `never_opened` — it is not, and NOTHING is on record: the onboarding has not
 * reached step 3.
 *
 * The last two are the whole reason this function exists rather than {@link accessClosed} alone. They are
 * the same empty set and they are opposite facts about Verso — one is a customer whose access was taken
 * away, the other a customer nobody has finished setting up — and until the backend recorded a date for
 * step 3's answers the console could only show them as one state, which read as paperwork for both.
 *
 * `recordedAt` is the server's `entitlementsRecordedAt`. A null one is "no answer on record" and is the
 * ONLY thing that produces `never_opened`: the console does not infer an answer from any other step having
 * been done, because what that would be inferring is the one fact nobody wrote down.
 */
export function accessState(
  entitlements: readonly string[] | null | undefined,
  recordedAt: string | null | undefined,
): "served" | "closed" | "never_opened" {
  if (!accessClosed(entitlements)) return "served";
  return recordedAt ? "closed" : "never_opened";
}

/**
 * The i18n key for the badge that names a tenant's access state, or null for a served customer — which
 * gets no badge, because "this is working" is not news on a directory of working customers.
 *
 * Here and not at the two call sites (the directory row and the tenant page's header) so that the list and
 * the page cannot come to call the same state by two different names.
 */
export function accessBadgeKey(
  entitlements: readonly string[] | null | undefined,
  recordedAt: string | null | undefined,
): string | null {
  switch (accessState(entitlements, recordedAt)) {
    case "served":
      return null;
    case "closed":
      return "tenants.access.badgeClosed";
    case "never_opened":
      return "tenants.access.badgeNeverOpened";
  }
}
